// Phase 7 — Category / Sub-Category admin screen, migrated to shadcn/Tailwind.
//
// UI only: antd Drawer/Form/Input/Modal/Switch/Upload + MUI Table/RadioGroup →
// shadcn Sheet + Input + ConfirmDialog + Switch + RadioGroup + shared DataTable,
// with an inline image dropzone. Every dispatch (categoryAndSubCategoryListAction,
// addCategoryAction, addSubCategoryAction, createOrUpdateAttachmentAction) and the
// upload/status/delete effect wiring are unchanged. The antd Form is dropped — the
// fields are already controlled by `addFormData`; the two antd validators are
// ported one-to-one into a local validate() gate.
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { ColumnDef } from '@tanstack/react-table'
import { MoreVertical, Pencil, Plus, Search, Trash2, UploadCloud, X } from 'lucide-react'
import { toast } from 'sonner'
import AppImages from '../Globals/AppImages'
import { useDispatch, useSelector } from 'react-redux'
import { addCategoryAction, addSubCategoryAction, categoryAndSubCategoryListAction, createOrUpdateAttachmentAction } from '../Store/actions/apparelAction'
import { deepCopyFunction, isArrayNotEmpty, toMb } from '../Utils/helpers'
import { deleteFileFromS3, uploadPhotoToAWS } from '../Components/SubComponents/awsUpload'
import AppConstant from '../Globals/AppConstants'
import ApparelMenu from '../Components/SubComponents/apparelMenu'
import { PageHeader } from '../shared/page-header'
import { DataTable } from '../shared/data-table'
import { LoadingOverlay } from '../shared/loading-overlay'
import { ConfirmDialog } from '../shared/confirm-dialog'
import { Button } from 'ui/button'
import { Input } from 'ui/input'
import { Label } from 'ui/label'
import { Switch } from 'ui/switch'
import { RadioGroup, RadioGroupItem } from 'ui/radio-group'
import { Sheet, SheetContent, SheetHeader, SheetTitle } from 'ui/sheet'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from 'ui/dropdown-menu'
import { Card, CardContent } from 'ui/card'
import { cn } from 'lib/utils'

const formDataTemp = {
  categoryName: "",
  categoryDescription: "",
  displayRank: null,
  isActive: 1,
  isDeleted: 0
}

const RANK_OPTIONS = [
  { value: 1, label: 'Mukizh Exclusive' },
  { value: 2, label: 'Celebrities Use' },
  { value: 3, label: 'Affordable Beauty' },
  { value: 4, label: 'Must-Try Products' },
]

const Category = () => {
  const location = useLocation();
  // Arriving from Audit Log: select the matching category (or, for a Sub
  // Category record, its parent category) once the list loads, and open
  // its edit Sheet — Category/Sub Category have a real edit UI, so Audit
  // Log goes straight to it. Guarded by a ref so a later list refetch
  // (e.g. after an edit) doesn't re-trigger and reopen the Sheet.
  const auditHandledRef = useRef(false);
  const [open, setOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCategory, setIsCategory] = useState(0)
  const [subCategoryData, setSubCategoryData] = useState<any>(null);
  const [categoryAndSubCategoryLoad, setCategoryAndSubCategoryLoad] = useState(false);
  const [imgList, setImgList] = useState<any>([]);
  const [singleImg, setSingleImg] = useState<any>(null);
  const [addFormData, setAddFormData] = useState(deepCopyFunction(formDataTemp));
  const [categoryAttachementObj, setCategoryAttachementObj] = useState<any>([]);
  const [categoryApiSuccess, setCategoryApiSuccess] = useState(false);
  const [subCategoryApiSuccess, setSubCategoryApiSuccess] = useState(false);
  const [imgType, setImgType] = useState<any>(1);
  const [categoryId, setCategoryId] = useState<any>(null);
  const [subCategoryId, setSubCategoryId] = useState<any>(null);
  const [statusChanged, setStatusChanged] = useState(false);
  const [updateAttachment, setUpdateAttachment] = useState<any>(false);
  const [nameError, setNameError] = useState<string>("");
  const [categorySearchText, setCategorySearchText] = useState("");
  const [subCategorySearchText, setSubCategorySearchText] = useState("");
  const ApparelReducerState = useSelector((state: any) => state.ApparelReducerState)
  const { categoryAndSubCategoryOnload, categoryAndSubCategoryData,
    addCategoryLoad, addSubCategoryLoad, addCategoryData,
    addSubCategoryData, createOrUpdateAttachmentLoad } = ApparelReducerState;
  const getCategoryList = categoryAndSubCategoryData?.getCategoryList;
  const dispatch = useDispatch();

  // Real-time, case-insensitive, partial-match filtering. Never mutates
  // getCategoryList/subCategories — always derives a fresh filtered array,
  // recomputed on every keystroke via useMemo.
  const includesText = (value: any, query: string) => String(value ?? "").toLowerCase().includes(query);

  const filteredCategoryList = useMemo(() => {
    const list = getCategoryList || [];
    const query = categorySearchText.trim().toLowerCase();
    if (!query) return list;
    return list.filter((c: any) => includesText(c?.categoryName, query));
  }, [getCategoryList, categorySearchText]);

  const filteredSubCategories = useMemo(() => {
    const list = subCategoryData?.subCategories || [];
    const query = subCategorySearchText.trim().toLowerCase();
    if (!query) return list;
    return list.filter((s: any) => includesText(s?.subCategoryName, query));
  }, [subCategoryData, subCategorySearchText]);

  const showDrawer = (data: any) => {
    setIsCategory(data)
    setImgType((data === 1 || data === 3) ? 3 : 4)
    setOpen(true);
  };

  const onClose = () => {
    setOpen(false);
    setIsModalOpen(false);
    setIsCategory(0);
    setCategoryAttachementObj([]);
    setImgList([]);
    setSingleImg(null);
    setSubCategoryId(null);
    setImgType(1);
    setNameError("");
    setAddFormData(formDataTemp);
  };

  const showModal = (data: any) => {
    setIsModalOpen(true);
    setIsCategory((data == 1) ? 1 : 2)
  };

  const handleCancel = () => {
    onClose();
    setIsModalOpen(false);
  };

  useEffect(() => {
    dispatch(categoryAndSubCategoryListAction({ isCategoryPage: 1 }));
    setCategoryAndSubCategoryLoad(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (categoryAndSubCategoryLoad && !categoryAndSubCategoryOnload) {
      let data = getCategoryList[0];
      if (categoryId) {
        data = (getCategoryList || [])?.find((x: any) => { return x?.categoryId == categoryId });
      }
      setSubCategoryData(data);
      setCategoryId(categoryId || getCategoryList[0].categoryId);
      setCategoryAndSubCategoryLoad(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryAndSubCategoryLoad, categoryAndSubCategoryOnload])

  useEffect(() => {
    if (auditHandledRef.current || !location.state?.auditRecordId || !isArrayNotEmpty(getCategoryList)) return;
    auditHandledRef.current = true;
    const recordId = location.state.auditRecordId;
    const matchedCategory = (getCategoryList || []).find((c: any) => c?.categoryId === recordId);
    if (matchedCategory) {
      setSubCategoryData(matchedCategory);
      setCategoryId(matchedCategory.categoryId);
      handleCategoryEdit(matchedCategory);
      return;
    }
    const parentCategory = (getCategoryList || []).find((c: any) =>
      (c?.subCategories || []).some((s: any) => s?.subCategoryId === recordId)
    );
    if (parentCategory) {
      // Look up the sub-category off `parentCategory` directly, not via
      // handleSubCategoryEdit's `subCategoryData` closure — the
      // setSubCategoryData call just above hasn't committed yet at this
      // point in the same effect run.
      const matchedSubCategory = (parentCategory.subCategories || []).find((s: any) => s?.subCategoryId === recordId);
      setSubCategoryData(parentCategory);
      setCategoryId(parentCategory.categoryId);
      if (matchedSubCategory) {
        prePopulate(matchedSubCategory);
        showDrawer(0);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getCategoryList])

  const prePopulate = (data: any) => {
    setCategoryAttachementObj(data?.categoryAttachment ? data?.categoryAttachment : data?.attachment ? data?.attachment : []);
    setAddFormData({
      ...addFormData,
      categoryName: data?.categoryName ? data?.categoryName : data?.subCategoryName,
      categoryDescription: data?.description,
      displayRank: data?.displayRank ? data?.displayRank : null,
      isActive: data?.isActive,
      isDeleted: data?.isDeleted ? data?.isDeleted : 0
    });
    setSubCategoryId(data?.subCategoryId ? data?.subCategoryId : null);
  }

  const handleDelete = (data: any, isCategory: any) => {
    setCategoryAttachementObj(data?.categoryAttachment ? data?.categoryAttachment : data?.attachment ? data?.attachment : []);
    setSubCategoryId(data?.subCategoryId ? data?.subCategoryId : null)
    setAddFormData({
      ...addFormData,
      categoryName: data?.categoryName ? data?.categoryName : data?.subCategoryName,
      categoryDescription: data?.description,
      displayRank: data?.displayRank ? data?.displayRank : null,
      isDeleted: AppConstant.one,
      isActive: 1
    });
    showModal(isCategory)
  }

  const handleDeleteApiCall = () => {
    if (categoryId && subCategoryId) {
      createOrUpdateSubCategoryApi();
    } else if (categoryId) {
      createOrUpdateCategoryApi();
    }
  }

  const imageUploadApi = (files: any, coverImage: any, isDeleted: any, isImageOrVideo: number) => {
    let obj = {
      fileUuid: files?.fileUuid,
      isImageOrVideo: isImageOrVideo,
      mimeType: files?.file?.mimeType ? files?.file?.mimeType : files?.file?.name?.substring(files?.file?.name?.lastIndexOf('.')),
      fileSize: `"${typeof files?.file?.fileSize == "string" ? files?.file?.fileSize : toMb(files?.file?.size)}"`,
      isDeleted: 0
    }
    setCategoryAttachementObj((prev: any) => [...prev, obj])
    return [obj];
  }

  const uploadAttachmentAws = (type: number) => {
    uploadPhotoToAWS(singleImg, imageUploadApi, type)
  }

  useEffect(() => {
    if (isArrayNotEmpty(imgList) && imgType != 1)
      uploadAttachmentAws(imgType)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imgList, imgType]);

  const createOrUpdateCategoryApi = () => {
    let payload = {
      categoryId: isCategory == 1 && addFormData.isDeleted == 0 ? "" : categoryId,
      categoryName: addFormData.categoryName,
      description: addFormData.categoryDescription,
      isActive: addFormData.isActive,
      isDeleted: addFormData.isDeleted,
      attachment: categoryAttachementObj
    }
    dispatch(addCategoryAction(payload))
    setCategoryApiSuccess(true)
  }

  useEffect(() => {
    if (categoryApiSuccess && !addCategoryLoad && typeof addCategoryData !== 'undefined') {
      setCategoryApiSuccess(false);
      if (addCategoryData.createOrUpdateCategoryList == AppConstant.NAME_ALREADY_EXISTS) {
        toast.warning(addCategoryData.createOrUpdateCategoryList);
      }
      else if (addCategoryData.createOrUpdateCategoryList == AppConstant.PRODUCT_IS_PRESENT) {
        toast.warning(addCategoryData.createOrUpdateCategoryList);
      }
      else {
        onClose();
        toast.success(isCategory == 3 ? "Category updated successfully" : "Category added successfully");
        dispatch(categoryAndSubCategoryListAction({ isCategoryPage: 1 }));
        setCategoryAndSubCategoryLoad(true)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryApiSuccess, addCategoryLoad, addCategoryData])

  const createOrUpdateSubCategoryApi = () => {
    let payload = {
      categoryId: categoryId,
      subCategoryId: subCategoryId ? subCategoryId : "",
      subCategoryName: addFormData.categoryName,
      description: addFormData.categoryDescription,
      displayRank: addFormData.displayRank,
      isActive: addFormData.isActive,
      isDeleted: addFormData.isDeleted,
      attachment: categoryAttachementObj
    }
    dispatch(addSubCategoryAction(payload))
    setSubCategoryApiSuccess(true)
  }

  useEffect(() => {
    if (subCategoryApiSuccess && !addSubCategoryLoad && typeof addSubCategoryData !== 'undefined') {
      setSubCategoryApiSuccess(false);
      if (addSubCategoryData.createOrUpdateSubCategoryList == AppConstant.NAME_ALREADY_EXISTS) {
        toast.warning(addSubCategoryData.createOrUpdateSubCategoryList);
      }
      else if (addSubCategoryData.createOrUpdateSubCategoryList == AppConstant.PRODUCT_IS_PRESENT) {
        toast.warning(addSubCategoryData.createOrUpdateSubCategoryList);
      }
      else {
        onClose();
        toast.success(isCategory == 2 ? "Sub Category added successfully" : "Sub Category updated successfully");
        dispatch(categoryAndSubCategoryListAction({ isCategoryPage: 1 }));
        setCategoryAndSubCategoryLoad(true)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subCategoryApiSuccess, addSubCategoryLoad, addSubCategoryData])

  const updateAttachmentApi = (image: any) => {
    let payload = {
      attachment: [image]
    }
    dispatch(createOrUpdateAttachmentAction(payload));
    setUpdateAttachment(true)
  }

  useEffect(() => {
    if (updateAttachment && !createOrUpdateAttachmentLoad) {
      setUpdateAttachment(false);
      setCategoryAttachementObj([]);
      setImgList([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateAttachment, createOrUpdateAttachmentLoad])

  const removeImg = (data: any, index: any) => {
    let deleteId = (index == 0 || index == 2) ? 4 : 3;
    deleteFileFromS3(data?.fileUuid, deleteId);
    let imageDelete = categoryAttachementObj.find((x: any) => x?.fileUuid == data?.fileUuid);
    imageDelete['isDeleted'] = 1;
    if (imageDelete?.attachmentId && imageDelete?.entityId) {
      updateAttachmentApi(imageDelete);
    } else {
      setCategoryAttachementObj([])
      setImgList([])
    }
  }

  // Ported from the legacy antd Upload customRequest (single-image + extension guard).
  const onPickImage = (file: File) => {
    if (imgList?.length < 1 && categoryAttachementObj.length != 1) {
      let imageType = file?.name?.split('.');
      let extension = imageType?.[imageType.length - 1]?.toLowerCase();
      if (['png', 'jpg', 'jpeg'].includes(extension)) {
        let obj = {
          previewImage: URL.createObjectURL(file),
          file: file
        }
        setImgList((prev: any) => prev ? [...prev, obj] : [obj])
        setSingleImg(obj);
      }
    } else {
      toast.error(`Please Select 1 Image Only`);
    }
  }

  // Ported from the antd Form.Item name validator (Name field).
  const validate = () => {
    const value = addFormData.categoryName;
    const label = (isCategory == 1 || isCategory == 3) ? "Category" : "SubCategory";
    if (value && String(value).trim() === '') {
      setNameError(`Please Enter Valid ${label} Name`);
      return false;
    }
    if (value === undefined || value === null || value === '') {
      setNameError(`Please Enter ${label} Name`);
      return false;
    }
    setNameError("");
    return true;
  }

  const handleApiCall = () => {
    if (!validate()) return;
    if (isCategory == 1 || isCategory == 3) {
      createOrUpdateCategoryApi();
    } else {
      createOrUpdateSubCategoryApi();
    }
  }

  const handleCategoryEdit = (data: any) => {
    prePopulate(data);
    showDrawer(3);
  }

  useEffect(() => {
    if (statusChanged) {
      if (categoryId && subCategoryId) {
        createOrUpdateSubCategoryApi()
      } else if (categoryId) {
        createOrUpdateCategoryApi()
      }
      setStatusChanged(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusChanged, subCategoryId, categoryId])

  const handleIsActive = (e: any, data: any) => {
    setCategoryAttachementObj(data?.categoryAttachment ? data?.categoryAttachment : data?.attachment ? data?.attachment : []);
    setSubCategoryId(data?.subCategoryId ? data?.subCategoryId : null)
    setAddFormData({
      ...addFormData,
      categoryName: data?.categoryName ? data?.categoryName : data?.subCategoryName,
      categoryDescription: data?.description,
      isDeleted: AppConstant.zero,
      isActive: e ? 1 : 0
    });
    setStatusChanged(true)
  }

  const handleSubCategoryEdit = (subCategory: any, drawer: any) => {
    let editSubCategoryData = subCategoryData?.subCategories.find((x: any) => x.subCategoryId == subCategory);
    prePopulate(editSubCategoryData);
    showDrawer(drawer);
  }

  const subCategoryColumns = useMemo<ColumnDef<any, any>[]>(
    () => [
      {
        id: 'subCategoryCode',
        header: 'ID',
        cell: ({ row }) => <span className="whitespace-nowrap font-medium">{row.original?.subCategoryCode || "-"}</span>,
        meta: { width: "110px" },
      },
      {
        id: 'name',
        header: 'Name',
        cell: ({ row }) => (
          <div className="flex min-w-0 items-center gap-2">
            <img src={AppImages?.dress} alt="" className="h-5 w-5 shrink-0" />
            <span className="truncate" title={row.original?.subCategoryName}>{row.original?.subCategoryName}</span>
          </div>
        ),
      },
      // { id: 'rank', header: 'Rank', cell: ({ row }) => <span className="tabular-nums">{row.original?.rankName ? row.original?.rankName : "-"}</span> },
      {
        id: 'status',
        header: 'Status',
        cell: ({ row }) => <Switch defaultChecked={row.original?.isActive == 1} onCheckedChange={(e) => handleIsActive(e, row.original)} />,
      },
      {
        id: 'action',
        header: 'Action',
        cell: ({ row }) => (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" aria-label="Row actions">
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => handleSubCategoryEdit(row.original?.subCategoryId, 0)}>
                <Pencil className="mr-2 h-4 w-4" /> Edit
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleDelete(row.original, 0)}>
                <Trash2 className="mr-2 h-4 w-4" /> Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [subCategoryData, addFormData]
  );

  const showRank = isCategory == 2 || isCategory == 0;
  const IMG_LOCATION = (isCategory == 2 || isCategory == 0) ? "sub_category/images/" : "category/images/";

  return (
    <div className="mz flex h-full flex-col overflow-hidden px-6 py-4">
      <ApparelMenu selectedKey="9" />
      <div className="grid flex-1 grid-cols-1 items-stretch gap-6 overflow-hidden lg:grid-cols-[400px_1fr]">
        {/* Category list — scrolls independently of the sub-category panel. */}
        <Card className="flex h-full flex-col overflow-hidden">
          <CardContent className="flex flex-1 flex-col overflow-hidden p-4">
            <PageHeader
              title="Category"
              className="mb-4 shrink-0"
              actions={
                <Button size="sm" onClick={() => showDrawer(1)}><Plus className="mr-1 h-4 w-4" /> Add</Button>
              }
            />
            <div className="relative mb-3 shrink-0">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                className="pl-9"
                placeholder="Search by Name"
                value={categorySearchText}
                onChange={(e) => setCategorySearchText(e.target.value)}
              />
            </div>
            <div className="space-y-1.5 overflow-y-auto">
              {categorySearchText.trim() && filteredCategoryList.length === 0 ? (
                <p className="px-3 py-2.5 text-sm text-muted-foreground">No results found</p>
              ) : null}
              {isArrayNotEmpty(filteredCategoryList) ? (filteredCategoryList || [])?.map((value: any) => (
                <div
                  key={value?.categoryId}
                  onClick={() => { setSubCategoryData(value); setCategoryId(value?.categoryId); setSubCategorySearchText("") }}
                  className={cn(
                    "flex cursor-pointer items-center justify-between gap-3 rounded-md px-3 py-2.5 transition-colors",
                    subCategoryData?.categoryId == value?.categoryId ? "bg-primary/10" : "hover:bg-muted"
                  )}
                >
                  <div className="flex min-w-0 flex-1 items-center gap-2.5 text-sm">
                    <img src={AppImages?.dress} alt="" className="h-5 w-5 shrink-0" />
                    <span className="shrink-0 rounded-md bg-muted px-1.5 py-0.5 text-[11px] font-medium text-muted-foreground">{value?.categoryCode}</span>
                    <span className="truncate" title={value?.categoryName}>{value?.categoryName}</span>
                  </div>
                  <div className="flex shrink-0 items-center gap-2" onClick={(e) => e.stopPropagation()}>
                    <Switch defaultChecked={value?.isActive == 1} onCheckedChange={(e) => handleIsActive(e, value)} />
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" aria-label="Row actions">
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => handleCategoryEdit(value)}>
                          <Pencil className="mr-2 h-4 w-4" /> Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleDelete(value, 1)}>
                          <Trash2 className="mr-2 h-4 w-4" /> Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              )) : (
                <p className="px-3 py-2.5 text-sm text-muted-foreground">
                  {categorySearchText.trim() ? "No categories found." : "No categories yet."}
                </p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Sub-category list — same height/scroll behavior as the category panel. */}
        <Card className="flex h-full flex-col overflow-hidden">
          <CardContent className="flex flex-1 flex-col overflow-hidden p-4">
            <PageHeader
              title={`Manage Category: ${subCategoryData?.categoryName ?? ""}`}
              className="mb-4 shrink-0"
              actions={
                <Button size="sm" onClick={() => showDrawer(2)}><Plus className="mr-1 h-4 w-4" /> Add Sub Category</Button>
              }
            />
            <div className="relative mb-3 w-full shrink-0">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                className="pl-9"
                placeholder="Search by Name"
                value={subCategorySearchText}
                onChange={(e) => setSubCategorySearchText(e.target.value)}
              />
            </div>
            <div className="overflow-y-auto">
              {subCategorySearchText.trim() && filteredSubCategories.length === 0 ? (
                <p className="px-1 py-2.5 text-sm text-muted-foreground">No results found</p>
              ) : (
                <DataTable columns={subCategoryColumns} data={filteredSubCategories} />
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Add / Edit drawer */}
      <Sheet open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
        <SheetContent side="right" className="flex w-full flex-col gap-0 p-0 sm:max-w-md">
          <SheetHeader className="border-b px-5 py-4">
            <SheetTitle>
              {isCategory == 1 ? "Add Category" : isCategory == 2 ? "Add Sub Category" : isCategory == 3 ? "Edit Category" : "Edit Sub Category"}
            </SheetTitle>
          </SheetHeader>
          <div className="flex flex-1 flex-col gap-4 overflow-y-auto p-5">
            <div className="space-y-1.5">
              <Label>Name <span className="text-destructive">*</span></Label>
              <Input
                value={addFormData.categoryName || ""}
                onChange={(e: any) => setAddFormData({ ...addFormData, categoryName: e?.target?.value })}
              />
              {nameError && <p className="text-xs text-destructive">{nameError}</p>}
            </div>
            <div className="space-y-1.5">
              <Label>Description</Label>
              <textarea
                className="flex min-h-[80px] w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                value={addFormData.categoryDescription || ""}
                onChange={(e: any) => setAddFormData({ ...addFormData, categoryDescription: e?.target?.value })}
              />
            </div>
            {showRank && (
              <div className="space-y-2">
                <Label>Rank</Label>
                <RadioGroup
                  className="flex flex-wrap gap-4"
                  value={String(addFormData?.displayRank ? addFormData?.displayRank : 0)}
                  onValueChange={(v) => setAddFormData({ ...addFormData, displayRank: v })}
                >
                  {RANK_OPTIONS.map((opt) => (
                    <div key={opt.value} className="flex items-center gap-2">
                      <RadioGroupItem value={String(opt.value)} id={`rank-${opt.value}`} />
                      <Label htmlFor={`rank-${opt.value}`} className="font-normal">{opt.label}</Label>
                    </div>
                  ))}
                </RadioGroup>
              </div>
            )}
            <div className="space-y-2">
              <Label>Image</Label>
              <ImageDropArea onPick={onPickImage} />
              <div className="flex flex-wrap gap-3">
                {(categoryAttachementObj || [])?.map((item: any, index: number) => (
                  <div key={index} className="relative h-24 w-24 overflow-hidden rounded-lg border">
                    <img
                      src={`${process.env.REACT_APP_AWS_CLOUD_FRONT_URL}/${process.env.REACT_APP_IMAGE_DESTINATION_BUCKET}${IMG_LOCATION}${item?.fileUuid}`}
                      alt="sample" className="h-full w-full object-cover"
                    />
                    <button
                      type="button"
                      onClick={() => removeImg(item, isCategory)}
                      aria-label="Remove image"
                      className="absolute right-1 top-1 rounded-md bg-background/80 p-0.5 text-muted-foreground hover:text-foreground"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
            <div className="mt-auto flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={onClose}>Cancel</Button>
              <Button disabled={addCategoryLoad || addSubCategoryLoad} onClick={handleApiCall}>
                {(isCategory == 3 || isCategory == 4 || isCategory == 0) ? "Save" : "Create"}
              </Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      {/* Delete confirm */}
      <ConfirmDialog
        open={isModalOpen}
        onOpenChange={(o) => { if (!o) handleCancel(); }}
        title={`Are you sure to Delete this${isCategory == 1 ? ' Category!' : ' Sub category!'}`}
        confirmLabel="Delete"
        destructive
        loading={addCategoryLoad || addSubCategoryLoad}
        onConfirm={handleDeleteApiCall}
      />

      <LoadingOverlay loading={addCategoryLoad || addSubCategoryLoad || categoryAndSubCategoryOnload} />
    </div>
  )
}

interface ImageDropAreaProps {
  onPick: (file: File) => void;
}

function ImageDropArea({ onPick }: ImageDropAreaProps) {
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const emit = (list: FileList | null) => {
    if (!list) return;
    Array.from(list).forEach((f) => onPick(f));
  };
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => inputRef.current?.click()}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") inputRef.current?.click(); }}
      onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
      onDragLeave={() => setDragging(false)}
      onDrop={(e) => { e.preventDefault(); setDragging(false); emit(e.dataTransfer.files); }}
      className={cn(
        "flex w-full cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border border-dashed p-6 text-center outline-none transition-colors",
        "hover:border-primary/60 focus-visible:ring-2 focus-visible:ring-ring",
        dragging ? "border-primary bg-primary/5" : "border-border"
      )}
    >
      <UploadCloud className="h-6 w-6 text-muted-foreground" />
      <span className="text-sm text-foreground">
        Drag &amp; Drop file here or <span className="text-primary underline">Browse</span>
      </span>
      <input
        ref={inputRef}
        type="file"
        accept=".png,.jpg,.jpeg"
        className="hidden"
        onChange={(e) => { emit(e.target.files); e.target.value = ""; }}
      />
    </div>
  );
}

export default Category
